import { useMutation, useQuery } from '@tanstack/react-query';

import { selectActiveAccount, useAuthStore } from '@features/auth';
import { queryClient } from '@lib/queryClient';
import type { ApiError } from '@/types/api.types';

import {
  inviteStaff,
  listStaff,
  revokeInvite,
  updateStaff,
  updateStaffStatus,
} from '../api/staffApi';
import type {
  InviteStaffRequest,
  StaffListData,
  StaffMember,
  UpdateStaffRequest,
} from '../types';

const staffKey = (accountId: string) => ['staff', accountId] as const;

type UpdateVariables = { staffId: string; body: UpdateStaffRequest };
type StatusVariables = { staffId: string; status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' };

function replaceInList(prev: StaffListData | undefined, updated: StaffMember): StaffListData {
  if (!prev) return { items: [updated], pagination: { page: 1, limit: 20, total: 1, totalPages: 1 } };
  return { ...prev, items: prev.items.map((m) => (m.staffId === updated.staffId ? updated : m)) };
}

export function useStaff() {
  const activeAccount = useAuthStore(selectActiveAccount);
  const accountId = activeAccount?.accountId ?? '';

  const query = useQuery<StaffListData, ApiError>({
    queryKey: staffKey(accountId),
    queryFn: () => listStaff(accountId),
    enabled: !!accountId,
  });

  const inviteMutation = useMutation<StaffMember, ApiError, InviteStaffRequest>({
    mutationFn: (body) => inviteStaff(accountId, body),
    onSuccess: (newMember) => {
      queryClient.setQueryData<StaffListData>(staffKey(accountId), (prev) =>
        prev
          ? {
              items: [...prev.items, newMember],
              pagination: { ...prev.pagination, total: prev.pagination.total + 1 },
            }
          : { items: [newMember], pagination: { page: 1, limit: 20, total: 1, totalPages: 1 } },
      );
    },
  });

  const updateMutation = useMutation<StaffMember, ApiError, UpdateVariables>({
    mutationFn: ({ staffId, body }) => updateStaff(accountId, staffId, body),
    onSuccess: (updated) => {
      queryClient.setQueryData<StaffListData>(staffKey(accountId), (prev) =>
        replaceInList(prev, updated),
      );
    },
  });

  const updateStatusMutation = useMutation<StaffMember, ApiError, StatusVariables>({
    mutationFn: ({ staffId, status }) => updateStaffStatus(accountId, staffId, { status }),
    onSuccess: (updated) => {
      queryClient.setQueryData<StaffListData>(staffKey(accountId), (prev) =>
        replaceInList(prev, updated),
      );
    },
  });

  const revokeInviteMutation = useMutation<StaffMember, ApiError, string>({
    mutationFn: (staffId) => revokeInvite(accountId, staffId),
    onSuccess: (updated) => {
      queryClient.setQueryData<StaffListData>(staffKey(accountId), (prev) =>
        replaceInList(prev, updated),
      );
    },
  });

  return {
    staff: query.data?.items ?? [],
    pagination: query.data?.pagination ?? null,
    isLoading: query.isLoading,
    fetchError: query.error ?? null,

    invite: inviteMutation.mutate,
    isInviting: inviteMutation.isPending,
    inviteError: inviteMutation.error,
    resetInviteError: () => inviteMutation.reset(),

    update: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
    resetUpdateError: () => updateMutation.reset(),

    updateStatus: updateStatusMutation.mutate,
    isUpdatingStatus: updateStatusMutation.isPending,
    statusError: updateStatusMutation.error,

    revokeInvite: revokeInviteMutation.mutate,
    isRevoking: revokeInviteMutation.isPending,
    revokeError: revokeInviteMutation.error,
  };
}
